import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { ElMessage } from 'element-plus'
import type { ApiResponse, PageResponse } from '@/types'

// 运行时配置，由 /config.js 注入（K8s ConfigMap 挂载）
const apiBaseURL = (window as any).__APP_CONFIG__?.apiBaseUrl || '/api/v1'

// Token storage keys
const USER_TOKEN_KEY = 'flashhub_token'
const ADMIN_TOKEN_KEY = 'admin_token'

// Create axios instance
const request: AxiosInstance = axios.create({
  baseURL: apiBaseURL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Get appropriate token based on current page
function getToken(): string | null {
  const isAdminPage = window.location.pathname.startsWith('/admin')
  const tokenKey = isAdminPage ? ADMIN_TOKEN_KEY : USER_TOKEN_KEY
  return localStorage.getItem(tokenKey)
}

// Get language
function getLanguage(): string {
  const isAdminPage = window.location.pathname.startsWith('/admin')
  const langKey = isAdminPage ? 'admin_language' : 'flashhub_language'
  return localStorage.getItem(langKey) || navigator.language || 'en'
}

// Request interceptor
request.interceptors.request.use(
  (config) => {
    // Don't override if Authorization is already set
    if (!config.headers.Authorization) {
      const token = getToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }
    
    // Set language
    const lang = getLanguage()
    config.headers['Accept-Language'] = lang
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
request.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    const res = response.data
    
    // If code is not 0, show error message
    if (res.code !== 0) {
      // For login/captcha requests, don't show toast (handled inline by the component)
      const requestUrl = response.config?.url || ''
      const isLoginRequest = requestUrl.includes('/login')
      const isCaptchaVerify = requestUrl.includes('/captcha/verify')
      const isSendCode = requestUrl.includes('/send-code')
      if (!isLoginRequest && !isCaptchaVerify && !isSendCode) {
        ElMessage.error(res.message || 'Error')
      }
      return Promise.reject(new Error(res.message || 'Error'))
    }
    
    return response
  },
  (error) => {
    if (error.response) {
      const status = error.response.status
      const message = error.response.data?.message || error.message
      const isAdminPage = window.location.pathname.startsWith('/admin')
      const isLoginPage = window.location.pathname.includes('/login')
      const requestUrl = error.config?.url || ''
      const isLoginRequest = requestUrl.includes('/login')
      
      if (status === 401) {
        // For login requests, just show error message (no redirect, no token clear)
        if (isLoginRequest) {
          ElMessage.error(message || 'Unauthorized')
          return Promise.reject(error)
        }
        
        // For other requests, clear token and redirect
        const tokenKey = isAdminPage ? ADMIN_TOKEN_KEY : USER_TOKEN_KEY
        localStorage.removeItem(tokenKey)
        
        // Redirect to appropriate login page (only if not already on login page)
        if (!isLoginPage) {
          window.location.href = isAdminPage ? '/admin/login' : '/login'
        }
        ElMessage.error(message || 'Unauthorized')
      } else if (status === 403) {
        // Forbidden - don't redirect, just show message
        ElMessage.error(message || 'Forbidden')
      } else {
        ElMessage.error(message || 'Error')
      }
    } else {
      ElMessage.error('Network error')
    }
    
    return Promise.reject(error)
  }
)

// API methods
export const api = {
  // Generic request methods
  get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<ApiResponse<T>>> {
    return request.get(url, config)
  },
  
  post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<ApiResponse<T>>> {
    return request.post(url, data, config)
  },
  
  put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<ApiResponse<T>>> {
    return request.put(url, data, config)
  },
  
  delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<ApiResponse<T>>> {
    return request.delete(url, config)
  },
  
  // Page request helper
  getPage<T = any>(url: string, params?: any): Promise<AxiosResponse<PageResponse<T>>> {
    return request.get(url, { params })
  },
  
  // Upload file
  upload<T = any>(url: string, formData: FormData, config?: AxiosRequestConfig): Promise<AxiosResponse<ApiResponse<T>>> {
    return request.post(url, formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  
}

export default request